import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { MobileBottomNav, MobileDrawer } from './mobile';
import HelpCenter from './HelpCenter';
import { useActivityTracker } from '../hooks/useActivityTracker';

// Context for mobile props
const MobileContext = createContext({
  isMobile: false,
  onMenuClick: () => {},
  pageTitle: ''
});

export const useMobileContext = () => useContext(MobileContext);

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  
  // Initialize activity tracking
  useActivityTracker();

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
        setDrawerOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close drawer when route changes
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  // Get page title based on route
  const getPageTitle = () => {
    const routes = {
      '/': 'Accueil',
      '/messagerie': 'Messagerie',
      '/incidents': 'Incidents',
      '/assemblees': 'Assemblées Générales',
      '/visites': 'Visites d\'immeuble',
      '/mon-compte': 'Mon Compte',
      '/immeubles': 'Mes Immeubles',
      '/coproprietaires': 'Mes Copropriétaires',
      '/equipe': 'Mon Équipe',
      '/prestataires': 'Mes Prestataires',
      '/evenements': 'Événements',
      '/annonces': 'Annonces',
      '/modules': 'Modules',
      '/mes-factures': 'Mes Factures',
      '/sous-comptes': 'Sous-comptes',
      '/admin': 'Administration',
      '/admin/cabinets': 'Cabinets',
    };
    return routes[location.pathname] || 'SyndicAppli';
  };

  const mobileContextValue = {
    isMobile,
    onMenuClick: toggleDrawer,
    pageTitle: getPageTitle()
  };

  return (
    <MobileContext.Provider value={mobileContextValue}>
      <div className="flex h-screen bg-gray-50">
        {/* Desktop Sidebar overlay */}
        {isMobile && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Desktop Sidebar - hidden on mobile by default */}
        <div className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out' : ''}
          ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
          ${!isMobile ? 'relative' : ''}
          hidden lg:block
        `}>
          <Sidebar onNavigate={isMobile ? closeSidebar : undefined} />
        </div>

        {/* Mobile Drawer */}
        {isMobile && (
          <MobileDrawer 
            isOpen={drawerOpen} 
            onClose={closeDrawer} 
          />
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden w-full">
          {/* Desktop Header */}
          <div className="hidden lg:block">
            <Header 
              onMenuClick={toggleSidebar} 
              showMenuButton={isMobile} 
            />
          </div>
          
          {/* Main Content Area */}
          <main className={`
            flex-1 overflow-y-auto 
            ${isMobile ? 'pb-20' : 'p-4 md:p-6'} 
          `}>
            {children}
          </main>

          {/* Mobile Bottom Navigation */}
          {isMobile && <MobileBottomNav />}
        </div>

        {/* Help Center - Always visible */}
        <HelpCenter />
      </div>
    </MobileContext.Provider>
  );
};

export default Layout;
